'use client';

import { useState, useEffect, useRef } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useSwrJobBulkApply } from '@hooks/useSwrApi/endpoints/job/useSwrJobBulkApply';
import { useTranslation } from 'next-i18next';
import { bjToast } from '@components/common';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

/**
 * @description Helper hook for bulk apply.
 * It provides a form to collect all the selected job slugs and the SWR mutation.
 *
 * @param successCallback function - Callback function that gets executed after bulk apply action successfully completed. Optional.
 *
 * Return:
 * - **formGroupName** - string - The form group name in which the job slugs are collected.
 * - **record** - RecordFunction - The record function from useBjForm.
 * - **handleStep** - HandleStepFunction - The handleStep function from useBjForm.
 * - **formRef** - React.RefObject<HTMLFormElement> - The form ref.
 * - **selectAll** - boolean | undefined - The select all state.
 * - **setSelectAll** - React.Dispatch<React.SetStateAction<boolean | undefined>> - The set select all state.
 * - **getSelectedJobs** - () => string[] - Get selected job slugs function.
 * - **bulkApplyAction** - () => void - Function that triggers SWR mutation hook for bulk apply.
 * - **isMutating** - Boolean. True if the bulk apply action is mutating, false if not.
 *
 * @example
 * const {
 *  formGroupName,
 *  record,
 *  handleStep,
 *  formRef,
 *  selectAll,
 *  setSelectAll,
 *  getSelectedJobs,
 *  bulkApplyAction,
 *  isMutating
 * } = useBulkApply();
 *
 * // Trigger bulk apply action
 * <Button isLoading={isMutating} onClick={bulkApplyAction}>
 *    {t('bulk.apply.text.button')}
 * </Button>
 *
 * // Select all / deselect all jobs
 * <label htmlFor="selectAll">
 *    <Input name="selectAll" type="checkbox" checked={!!selectAll} onClick={() => setSelectAll(!selectAll)} />
 * </label>
 *
 * // Form wrapper on job cards
 * <form noValidate onSubmit={handleStep} ref={formRef}>
 *   {jobList.map((job) => (
 *    <CardJobTalent
 *      bulkApply={{
*          record,
*          name: formGroupName,
*          setSelectAll,
*       }}
 *      ...otherProps
 *    />
 *   )}
 * </form>
 */
export const useBulkApply = (successCallback?: () => void) => {
  const formGroupName = 'jobSlugs';

  // Get current locale
  const { locale } = useExtendedRouter();

  // Translation
  const { t } = useTranslation('common');

  // State for checkboxes in bulk operations; we're using the 'undefined' state to handle the select all and then uncheck some
  // of the checkboxes, without triggering the check/uncheck all.
  // ***********************************************
  const [selectAll, setSelectAll] = useState<boolean | undefined>(true);

  // Form for bulk operations
  // ***********************************************
  const {
    record,
    handleStep,
    selectAllCheckboxes,
    getFormData,
  } = useBjForm();

  const formRef = useRef<HTMLFormElement>(null);

  // SWR
  // ***********************************************
  const {
    trigger,
    isMutating,
  } = useSwrJobBulkApply(locale, successCallback, t('global.label.bulk.apply.succesfull'));

  // Get selected jobs
  // ***********************************************
  const getSelectedJobs = () => {
    const formData = getFormData(formRef);
    return formData?.[formGroupName as keyof typeof formData] as string[] || [];
  };

  // Effects
  // ***********************************************
  // Handle bulk select/deselect
  useEffect(() => {
    if (selectAll !== undefined) {
      selectAllCheckboxes(selectAll, [formGroupName]);
    }
  }, [selectAll, selectAllCheckboxes, formGroupName]);

  // Bulk Apply Action
  // ***********************************************
  const bulkApplyAction = () => {
    const selectedJobs = getSelectedJobs();
    if (!selectedJobs.length) {
      bjToast.error(t('bulk.apply.select.required'), { id: 'bulkApplySelectRequired' });
    } else {
      void trigger(selectedJobs);
    }
  };

  // Return
  // ***********************************************
  return {
    formGroupName,
    record,
    handleStep,
    formRef,
    selectAll,
    setSelectAll,
    getSelectedJobs,
    bulkApplyAction,
    isMutating,
  };
};
